import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const initial = {
  name: "",
  email: "",
  phone: "",
  number_of_learners: 1,
  age: "",
  interested_language: "Hausa",
  comments: "",
};

export default function TrialForm({ onSuccess, compact = false }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/trial-bookings", {
        ...form,
        number_of_learners: Number(form.number_of_learners) || 1,
      });
      toast.success("Free trial booked! Watch your inbox.");
      setDone(true);
      setForm(initial);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="trial-success"
        className="rounded-3xl border border-border bg-card p-8 text-center"
      >
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-primary" strokeWidth={1.5} />
        <h4 className="mb-2 text-2xl">Free class booked.</h4>
        <p className="text-muted-foreground">
          We'll email your Google Meet link before your first lesson.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="trial-form"
      className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}
    >
      <div className={compact ? "" : "md:col-span-2"}>
        <Label htmlFor="t-name">Full name</Label>
        <Input
          id="t-name"
          data-testid="t-name"
          required
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="t-email">Email</Label>
        <Input
          id="t-email"
          type="email"
          data-testid="t-email"
          required
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="t-phone">Phone</Label>
        <Input
          id="t-phone"
          data-testid="t-phone"
          required
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="t-num">Number of learners</Label>
        <Input
          id="t-num"
          type="number"
          min="1"
          data-testid="t-num"
          value={form.number_of_learners}
          onChange={(e) => update("number_of_learners", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="t-age">Age(s)</Label>
        <Input
          id="t-age"
          data-testid="t-age"
          placeholder="e.g. 8, 11"
          value={form.age}
          onChange={(e) => update("age", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <Label>Interested language</Label>
        <Select
          value={form.interested_language}
          onValueChange={(v) => update("interested_language", v)}
        >
          <SelectTrigger data-testid="t-language" className="mt-1.5 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Hausa">Hausa</SelectItem>
            <SelectItem value="Yoruba">Yoruba (coming)</SelectItem>
            <SelectItem value="Igbo">Igbo (coming)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <Label htmlFor="t-comments">Comments (optional)</Label>
        <Textarea
          id="t-comments"
          data-testid="t-comments"
          rows={3}
          value={form.comments}
          onChange={(e) => update("comments", e.target.value)}
          className="mt-1.5 rounded-xl"
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <Button
          type="submit"
          disabled={loading}
          data-testid="t-submit"
          className="w-full rounded-full bg-[hsl(41_85%_55%)] py-6 text-base font-medium text-[hsl(150_20%_15%)] hover:bg-[hsl(41_85%_50%)]"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Book My Free Class
        </Button>
      </div>
    </form>
  );
}
